import React, { useState, useEffect } from 'react';
import { Card, Button, Col, Form, Row } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider, { Search, CSVExport } from 'react-bootstrap-table2-toolkit';
import paginationFactory, { PaginationProvider, PaginationListStandalone, SizePerPageDropdownStandalone } from 'react-bootstrap-table2-paginator';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Fetcher } from '../../Helpers/fetcher.js';
import { Notify } from '../../Helpers/notification.js';
import StatsModal from './StatsModal.js';

const UserWise = ({ app }) => {
    const today = new Date().toISOString().split('T')[0];
    const [statisticSummaryData, setStatisticSummaryData] = useState([]);
    const [userId, setUserId] = useState('');
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(true);
    const [showStatModal, setShowStatModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [noDataDisplayMsg, setNoDataDisplayMsg] = useState('No data available');

    const calculateStats = (user) => {
        if (!user) return {
            totalNumbers: 0,
            totalCampaignList: 0,
            callsDialed: 0,
            pendingCalls: 0,
            dndCount: 0,
            connectedCalls: 0
        };

        const totalNumbers = user.numbersUploaded || 0;
        const callsDialed = user.numbersProcessed || 0;
        const dndCount = user.dndCount || 0;
        const connectedCalls = user.callsConnected || 0;
        const totalCampaignList = user.totalCampaigns || 0;
        const pendingCalls = totalNumbers - dndCount - callsDialed;

        return { totalNumbers, totalCampaignList, callsDialed, pendingCalls, dndCount, connectedCalls };
    };

    const fetchUserStatistics = async () => {
        setLoading(true);

        const payload = {
            startDate,
            endDate,
            userId: app.userId,
            filterBy: userId ? "user_id" : "",
            filterData: userId
        };

        try {
            const res = await Fetcher.post(`${app.apiPath}/api/obd/userwise/voice/campaigns/summary`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            const dataArray = Array.isArray(res) ? res : [];
            setStatisticSummaryData(dataArray);

            if (!dataArray.length) {
                setNoDataDisplayMsg('No data available for the selected date range');
            }
        } catch (err) {
            console.error('Error fetching User Statistics:', err);
            Notify.notify('Error fetching data');
            setNoDataDisplayMsg('Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (setter) => (date) => {
        setter(date ? date.toISOString().split('T')[0] : '');
    };

    const handleViewClick = (user) => {
        setSelectedUser(user);
        setShowStatModal(true);
    };

    const searchStatSummary = (e) => {
        e.preventDefault();
        fetchUserStatistics();
    };

    const statisticColumns = [
        { dataField: 'userId', text: 'User ID', sort: true },
        { dataField: 'username', text: 'Username', sort: true },
        { dataField: 'totalCampaigns', text: 'Total Campaigns', sort: true },
        { dataField: 'numbersUploaded', text: 'Total Numbers', sort: true },
        { dataField: 'numbersProcessed', text: 'Calls Dialed', sort: true },
        { dataField: 'dndCount', text: 'DnD Count', sort: true },
        { dataField: 'callsConnected', text: 'Connected Calls', sort: true },
        {
            dataField: 'pendingCalls',
            text: 'Pending Calls',
            formatter: (_, row) => row.numbersUploaded - row.dndCount - row.numbersProcessed,
            csvFormatter: (_, row) => row.numbersUploaded - row.dndCount - row.numbersProcessed
        },
        {
            dataField: 'action',
            text: 'Action',
            formatter: (_, row) => (
                <Button variant="primary" size="sm" onClick={() => handleViewClick(row)}> View</Button>
            ),
            csvExport: false
        }
    ];

    const paginationOptions = {
        custom: true,
        totalSize: statisticSummaryData.length,
        sizePerPage: 5,
        sizePerPageList: [5]
    };

    useEffect(() => {
        fetchUserStatistics();
    }, []);

    const { SearchBar, ClearSearchButton } = Search;
    const { ExportCSVButton } = CSVExport;

    const stats = selectedUser ? calculateStats(selectedUser) : {};

    return (
        <Col sm={12}>
            <Card>
                <Card.Header>User-wise Campaign Summary</Card.Header>
                <Card.Body>

                    <Form className="mb-3" onSubmit={searchStatSummary}>
                        <Row className="align-items-end">
                            <Col sm={3}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker
                                        className="form-control"
                                        dateFormat="yyyy-MM-dd"
                                        selected={startDate ? new Date(startDate) : null}
                                        onChange={handleDateChange(setStartDate)}
                                        placeholderText="Start Date"
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={3}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <DatePicker
                                        className="form-control"
                                        dateFormat="yyyy-MM-dd"
                                        selected={endDate ? new Date(endDate) : null}
                                        onChange={handleDateChange(setEndDate)}
                                        placeholderText="End Date"
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={3}>
                                <Form.Group>
                                    <Form.Label>User ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter User ID"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={2}>
                                <Button variant="primary" type="submit">Search</Button>
                                <Button variant="link" className="p-0 ms-2 text-decoration-none"
                                    onClick={() => {
                                        setStartDate(today);
                                        setEndDate(today);
                                        setUserId('');
                                    }}
                                >
                                    Clear
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                        {({ paginationProps, paginationTableProps }) => (
                                <ToolkitProvider
                                    keyField="userId"
                                    data={statisticSummaryData}
                                    columns={statisticColumns}
                                    search
                                    bootstrap4
                                    exportCSV={{
                                        fileName: 'User_Wise_Campaign_Summary.csv'
                                    }}
                                >

                                {props => (
                                    <div>
                                        <Row className="align-items-center mb-3">
                                            <Col md="auto">
                                                Show <SizePerPageDropdownStandalone {...paginationProps} /> items
                                            </Col>
                                            <Col md="auto">
                                                <SearchBar {...props.searchProps} />
                                            </Col>
                                            <Col md="auto">
                                                <ClearSearchButton {...props.searchProps} className="btn-primary" />
                                            </Col>
                                            <Col md="auto">
                                                <ExportCSVButton className="export-csv btn-primary ml-2" {...props.csvProps}>Export to CSV</ExportCSVButton>
                                            </Col>
                                        </Row>

                                        <BootstrapTable
                                            bordered
                                            striped
                                            condensed
                                            wrapperClasses="table-responsive"
                                            {...props.baseProps}
                                            {...paginationTableProps}
                                            noDataIndication={() => <div>{noDataDisplayMsg}</div>}
                                        />

                                        <PaginationListStandalone {...paginationProps} />
                                    </div>
                                )}
                            </ToolkitProvider>
                        )}
                    </PaginationProvider>

                    {loading && <div className="fetching-data"><img alt="" src="/assets/loading.gif" /></div>}
                </Card.Body>
            </Card>

            {selectedUser && (
                <StatsModal
                    show={showStatModal}
                    onHide={() => setShowStatModal(false)}
                    username={selectedUser.username}
                    {...stats}
                />
            )}
        </Col>
    );
};

export default UserWise;
